import type { LDContext, LDContextCommon, LDMultiKindContext } from 'launchdarkly-js-client-sdk';
import { getLDClient } from './ld-client-ref';

type UserTraits = Record<string, unknown>;

// `in` instead of a cast so we're safe against the legacy LDUser shape (no
// `kind` field) and an undefined context from a not-yet-initialized client.
function isMultiKindContext(context: LDContext | undefined): context is LDMultiKindContext {
  return typeof context === 'object' && context !== null && 'kind' in context && context.kind === 'multi';
}

/**
 * Updates the LD user context to identify the active user. Preserves other
 * context kinds (ecomm-dispensary, ecomm-enterprise, application, ...) and
 * merges new traits into the existing user sub-context so attributes set by
 * the feature-flags controllers survive. Fire-and-forget, never throws.
 */
export function identifyLDUser(userKey: string, traits: UserTraits = {}): void {
  if (!userKey) return;
  const client = getLDClient();
  if (!client) return;

  try {
    const current = client.getContext();
    let next: LDContext;
    if (isMultiKindContext(current)) {
      // `key` after spread so the explicit userKey wins over any traits.key.
      const existingUser = (current.user ?? {}) as LDContextCommon;
      next = { ...current, user: { ...existingUser, ...traits, key: userKey } };
    } else {
      next = { kind: 'user', ...traits, key: userKey };
    }

    // Log but don't throw: identify failures shouldn't break the calling flow.
    // The breadcrumb is what lets you debug stale flag evaluations caused by a
    // network blip or context validation error at identify time.
    client.identify(next).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[LD] identifyLDUser failed:', err);
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[LD] identifyLDUser threw before identify:', err);
  }
}
